import React from 'react';

/**
 * اجزای مشترک ظاهری پرسشنامه.
 *
 * قاعدهٔ بصری این صفحه: بخش‌ها با **بوردر نازک** از هم جدا می‌شوند، نه با
 * پس‌زمینهٔ رنگی. تنها جایی که سطح رنگی داریم هیروی بالای صفحه است.
 */

export const BRAND = '#1C3B9B';
export const HAIRLINE = '#E8EDF7';

/**
 * طیف پاسخ: واگرا با میانهٔ خنثی — دو قطب متضاد (گرم/سرد) و یک خاکستری وسط.
 * «کاملاً موافقم» یعنی مسئله شدیداً وجود دارد، پس قطب گرم؛ «مخالفم» قطب سرد.
 * رنگ هیچ‌وقت تنها حامل معنا نیست: شمارهٔ گزینه و برچسب فارسی همیشه کنارش هستند.
 */
export const SCALE_COLORS = ['#B3261E', '#C2612F', '#64748B', '#2E8B6B', '#14743A'];

/** پس‌زمینهٔ محو هالهٔ صفحه */
export const AssessmentBackdrop: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="relative min-h-screen overflow-hidden bg-[#FAFBFE]">
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 h-[460px]"
      style={{
        background:
          'radial-gradient(820px 340px at 80% -10%, rgba(28,59,155,0.10), transparent 62%), radial-gradient(660px 320px at 10% 2%, rgba(109,40,217,0.08), transparent 60%)',
      }}
    />
    <div className="relative">{children}</div>
  </div>
);

/** کارت پایه: سفید، بوردر مویی، سایهٔ نرم */
export const Panel: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div
    className={`rounded-[26px] border bg-white shadow-[0_18px_46px_-34px_rgba(15,23,42,0.42)] ${className}`}
    style={{ borderColor: HAIRLINE }}
  >
    {children}
  </div>
);

/** جداکنندهٔ داخل کارت — همیشه بوردر مویی، بدون تغییر پس‌زمینه */
export const PanelDivider: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`border-t ${className}`} style={{ borderColor: HAIRLINE }} />
);

/** حلقهٔ درصد پیشرفت */
export const ProgressRing: React.FC<{ value: number; size?: number }> = ({ value, size = 52 }) => {
  const clamped = Math.max(0, Math.min(100, value));
  const stroke = 5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <defs>
          <linearGradient id="ring-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#1C3B9B" />
            <stop offset="100%" stopColor="#6D28D9" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={HAIRLINE}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="url(#ring-grad)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (clamped / 100) * circumference}
          style={{ transition: 'stroke-dashoffset 700ms cubic-bezier(0.22,1,0.36,1)' }}
        />
      </svg>
      <span className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="text-[13px] font-black tabular-nums text-slate-900">
          {clamped.toLocaleString('fa-IR')}
        </span>
        <span className="mt-0.5 text-[8px] font-black text-slate-400">درصد</span>
      </span>
    </div>
  );
};

// ─── طیف پاسخ (Likert) ───────────────────────────────────────────────────────

interface LikertScaleProps {
  labels: string[];
  value?: number;
  onChange: (value: number) => void;
  name: string;
}

/** گرادیان ریل از روی همان پنج رنگ طیف */
const RAIL_GRADIENT = `linear-gradient(to left, ${SCALE_COLORS.map(
  (c) => `${c}33`,
).join(', ')})`;

/**
 * پنج گزینه روی یک ریل پیوسته می‌نشینند تا خواننده بفهمد بین دو قطب
 * دارد یک درجه انتخاب می‌کند، نه اینکه پنج دکمهٔ بی‌ربط جلویش باشد.
 */
export const LikertScale: React.FC<LikertScaleProps> = ({ labels, value, onChange, name }) => (
  <div role="radiogroup" aria-label={name}>
    <div className="relative">
      {/* ریل پشت گره‌ها؛ top با مرکز دایره‌های ۴۴px هم‌تراز است */}
      <div
        aria-hidden
        className="absolute right-[10%] left-[10%] top-[21px] h-[3px] rounded-full"
        style={{ background: RAIL_GRADIENT }}
      />

      <div className="relative grid grid-cols-5 gap-1">
        {labels.map((label, index) => {
          const optionValue = index + 1;
          const active = value === optionValue;
          const color = SCALE_COLORS[index] ?? BRAND;

          return (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(optionValue)}
              className="group flex flex-col items-center gap-1.5 rounded-2xl px-0.5 pb-1 outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{ ...(active ? { ['--tw-ring-color' as any]: `${color}55` } : {}) }}
            >
              <span
                className={`relative flex h-[44px] w-[44px] items-center justify-center rounded-full border-2 text-[13.5px] font-black tabular-nums transition-all duration-300 ${
                  active ? 'scale-[1.14] border-transparent text-white' : 'bg-white'
                }`}
                style={
                  active
                    ? { backgroundColor: color, boxShadow: `0 12px 22px -10px ${color}D9` }
                    : { borderColor: `${color}44`, color: `${color}CC` }
                }
              >
                {optionValue.toLocaleString('fa-IR')}
                {active ? (
                  <span
                    aria-hidden
                    className="absolute -inset-1.5 -z-10 rounded-full"
                    style={{ backgroundColor: `${color}1F` }}
                  />
                ) : null}
              </span>
              <span
                className="text-center text-[9.5px] font-black leading-tight transition-colors sm:text-[11px]"
                style={{ color: active ? color : '#8A94A6' }}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  </div>
);

/**
 * راهنمای طیف — یک بار بالای هر بخش، نه زیر تک‌تک سوال‌ها.
 * تکرارش برای شش سوال پشت سر هم فقط شلوغی می‌سازد.
 */
export const ScaleLegend: React.FC<{ labels: string[] }> = ({ labels }) => (
  <div className="flex items-center gap-2.5 text-[10.5px] font-black">
    <span style={{ color: SCALE_COLORS[0] }}>{labels[0]}</span>
    <span
      aria-hidden
      className="h-[3px] flex-1 rounded-full"
      style={{ background: `linear-gradient(to left, ${SCALE_COLORS.join(', ')})`, opacity: 0.5 }}
    />
    <span style={{ color: SCALE_COLORS[SCALE_COLORS.length - 1] }}>
      {labels[labels.length - 1]}
    </span>
  </div>
);

// ─── نوار مرحله‌ها ───────────────────────────────────────────────────────────

interface StepStripProps {
  sections: Array<{ id: string; title: string }>;
  current: number;
  completed: boolean[];
  onJump: (index: number) => void;
}

/**
 * قطعه‌های چسبیده به هم که با هم یک نوار پیوسته می‌سازند — نه خط‌چین.
 * فقط بخش‌های تکمیل‌شده و بخش فعلی قابل پرش‌اند.
 */
export const StepStrip: React.FC<StepStripProps> = ({ sections, current, completed, onJump }) => (
  <div className="flex items-center gap-[2px]">
    {sections.map((section, index) => {
      const isCurrent = index === current;
      const isDone = completed[index];
      const reachable = isDone || index <= current;

      return (
        <button
          key={section.id}
          type="button"
          disabled={!reachable}
          onClick={() => reachable && onJump(index)}
          /* عنوان بخش نه در tooltip و نه برای اسکرین‌ریدر نمایش داده نمی‌شود
             تا پاسخ‌دهنده در حین آزمون موضوع بخش را نفهمد */
          title={`بخش ${index + 1}`}
          aria-current={isCurrent ? 'step' : undefined}
          className={`group relative h-[5px] flex-1 overflow-hidden rounded-full transition-colors ${
            reachable ? 'cursor-pointer' : 'cursor-default'
          }`}
          style={{
            background: isCurrent ? BRAND : isDone ? '#A9B8E8' : HAIRLINE,
          }}
        >
          <span className="sr-only">{`بخش ${index + 1}`}</span>
        </button>
      );
    })}
  </div>
);

// ─── مقیاس بله/خیر ────────────────────────────────────────────────────────────

/** بله = ۱ (مسئله وجود دارد) و خیر = ۵ — همان ستون value لیکرت، بدون تغییر سرور */
export const YES_NO_VALUES = { yes: 1, no: 5 } as const;

interface YesNoScaleProps {
  value?: number;
  onChange: (value: number) => void;
  name: string;
}

/**
 * دو دکمهٔ بزرگ، بدون ریل. رنگ‌ها دو قطب همان طیف لیکرت‌اند تا حاشیهٔ رنگی
 * کارت سوال (SCALE_COLORS[value-1]) برای هر دو مقیاس یکسان کار کند.
 */
export const YesNoScale: React.FC<YesNoScaleProps> = ({ value, onChange, name }) => (
  <div role="radiogroup" aria-label={name} className="grid grid-cols-2 gap-2.5">
    {[
      { label: 'بله', optionValue: YES_NO_VALUES.yes, color: SCALE_COLORS[0] },
      { label: 'خیر', optionValue: YES_NO_VALUES.no, color: SCALE_COLORS[4] },
    ].map((option) => {
      const active = value === option.optionValue;
      return (
        <button
          key={option.label}
          type="button"
          role="radio"
          aria-checked={active}
          onClick={() => onChange(option.optionValue)}
          className={`rounded-2xl border-2 px-4 py-3.5 text-[15px] font-black transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
            active ? 'scale-[1.02] border-transparent text-white' : 'bg-white hover:-translate-y-0.5'
          }`}
          style={
            active
              ? {
                  backgroundColor: option.color,
                  boxShadow: `0 14px 26px -12px ${option.color}D9`,
                  ['--tw-ring-color' as any]: `${option.color}55`,
                }
              : { borderColor: `${option.color}55`, color: option.color }
          }
        >
          {option.label}
        </button>
      );
    })}
  </div>
);
